import React from 'react';
import AlertBase, {
  AlertBaseProps,
  alertBaseVerticalPaddingInThemeSpacing,
} from '../AlertBase/AlertBase';
import {
  styled,
  useThemeProps,
  alertClasses as muiAlertClasses,
  capitalize,
  ComponentsProps,
  ComponentsVariants,
} from '@mui/material';
import { getAlertBaseStyledOverridesResolver } from '../AlertBase/styledAlertBaseOverridesResolver';
import { getAlertBaseUtilityClass } from '../AlertBase/alertBaseClasses';
import { getToken } from '@workiva/unify-tokens';
import {
  AlertBlockedProps,
  UnsupportedProps as AlertUnsupportedProps,
} from '../Alert/Alert';
import {
  defaultAlertBaseContextValue,
  IAlertBaseContext,
} from '../AlertBase/AlertBaseContext';
import bannerClasses, { BannerClassKey } from './bannerClasses';
import clsx from 'clsx';

// Module augmentation to add this component to the theme for component overrides / default props
declare module '@mui/material/styles' {
  interface ComponentNameToClassKey {
    UnifyBanner: BannerClassKey;
  }

  interface ComponentsPropsList {
    UnifyBanner: BannerProps;
  }

  interface Components {
    UnifyBanner?: {
      defaultProps?: ComponentsProps['UnifyBanner'];
      variants?: ComponentsVariants['UnifyBanner'];
    };
  }
}

export type BannerProps = Omit<
  AlertBaseProps,
  | keyof AlertUnsupportedProps
  | 'components'
  | 'componentsProps'
  | 'iconMapping'
  | AlertBlockedProps
>;

const UnifyBannerRoot = styled(AlertBase, {
  name: 'UnifyBanner',
  slot: 'Root',
  overridesResolver: getAlertBaseStyledOverridesResolver('root'),
})<
  Omit<BannerProps, 'severity'> & Required<Pick<IAlertBaseContext, 'severity'>>
>(({ severity, theme }) => {
  const severityToken = severity === 'info' ? 'primary' : severity;
  // In order for Banner to have the same intrinsic height as Alert, we need
  // to add 1px extra padding to the top/bottom since Banner has no border
  // to contribute those 2px to the box-model height.
  const computedVerticalPaddingInPx =
    alertBaseVerticalPaddingInThemeSpacing * theme.spacingScalingFactor + 1;

  return {
    minWidth: '500px',
    paddingTop: computedVerticalPaddingInPx,
    paddingBottom: computedVerticalPaddingInPx,

    [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
      color: getToken(
        `Core/Text/text-${severity === 'warning' ? 'onWarning' : 'onColor'}`
      ),
      backgroundColor: getToken(
        `Component/Banner/banner-${severityToken}-background`
      ),

      [`.${muiAlertClasses.message}`]: {
        '& a, & [role="link"]': {
          color: 'currentcolor',
          textDecoration: 'underline',
        },
      },
    },
  };
});

/**
 * DO NOT use this component directly - work in progress to use Banners only at the shell level for now.
 *
 * A banner displays a prominent message at the top of the screen.
 */
export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  function Banner(inProps, forwardedRef) {
    const props = useThemeProps({
      props: inProps,
      name: 'UnifyBanner',
    });
    const {
      severity: severityIn,
      children,
      onClose,
      className,
      ...rest
    } = props;

    const rootClasses = clsx([bannerClasses.root, className]);
    const severity = severityIn ?? defaultAlertBaseContextValue.severity;

    return (
      <UnifyBannerRoot
        ref={forwardedRef}
        className={rootClasses}
        onClose={onClose}
        severity={severity}
        elevation={4}
        {...rest}
      >
        {children}
      </UnifyBannerRoot>
    );
  }
);

export default Banner;
